// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
import { Controller } from "@hotwired/stimulus"
import { resolveItemsPerView, slideBasis, nearestIndex, stepIndex, positionCount } from "./shared/carousel_layout"

/**
 * Carousel Controller
 * @gem rapid_rails_ui
 *
 * Handles slide rotation, indicator + arrow nav, keyboard nav, auto-rotate
 * with hover/visibility/reduced-motion pauses, loop wraparound, and
 * responsive items-per-view via a CSS variable on the root.
 *
 * Targets: track, slide, indicator, prev, next, liveRegion
 *
 * Values:
 *   autoRotate (Boolean, default false)
 *   interval (Number, default 5000)
 *   loop (Boolean, default true)
 *   pauseOnHover (Boolean, default true)
 *   respectReducedMotion (Boolean, default true) — when false, motion runs
 *     regardless of the OS `prefers-reduced-motion: reduce` setting. Consumer
 *     opt-out; accessibility responsibility shifts to the consumer.
 *   itemsPerView (String, default "1") — JSON-encoded: "1" or {"base":1,"md":3}
 */
export default class extends Controller {
  static targets = ["track", "slide", "indicator", "prev", "next", "liveRegion"]
  static values = {
    autoRotate: { type: Boolean, default: false },
    interval: { type: Number, default: 5000 },
    loop: { type: Boolean, default: true },
    pauseOnHover: { type: Boolean, default: true },
    respectReducedMotion: { type: Boolean, default: true },
    itemsPerView: { type: String, default: "1" }
  }

  connect() {
    this._activeIndex = 0
    this._perView = 1
    this._timer = null
    this._userPaused = false
    this._hoverPaused = false
    this._reducedMotion = this._matchesReducedMotion()
    this._resizeHandler = this._debounce(() => this._resolveItemsPerView(), 100)
    this._visibilityHandler = () => this._onVisibilityChange()

    this._resolveItemsPerView()
    this._setupScrollSync()
    this._updateActive(0, { announce: false })

    window.addEventListener("resize", this._resizeHandler)
    document.addEventListener("visibilitychange", this._visibilityHandler)

    if (this.autoRotateValue && !this._motionSuppressed()) {
      this._startTimer()
    }
  }

  disconnect() {
    this._stopTimer()
    if (this.hasTrackTarget && this._scrollHandler) {
      this.trackTarget.removeEventListener("scroll", this._scrollHandler)
    }
    if (this._scrollFrame) window.cancelAnimationFrame(this._scrollFrame)
    window.removeEventListener("resize", this._resizeHandler)
    document.removeEventListener("visibilitychange", this._visibilityHandler)
  }

  // ============================================================================
  // PUBLIC ACTIONS
  // ============================================================================

  next() {
    this._step(1)
  }

  prev() {
    this._step(-1)
  }

  goTo(event) {
    const idx = parseInt(event?.params?.index, 10)
    if (Number.isNaN(idx)) return
    this._scrollTo(idx)
  }

  pause() {
    if (!this.pauseOnHoverValue) return
    this._hoverPaused = true
    this._stopTimer()
  }

  resume() {
    if (!this.pauseOnHoverValue) return
    this._hoverPaused = false
    if (this.autoRotateValue && !this._userPaused && !this._motionSuppressed()) {
      this._startTimer()
    }
  }

  onKeydown(event) {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault()
        this.next()
        break
      case "ArrowLeft":
        event.preventDefault()
        this.prev()
        break
      case "Home":
        event.preventDefault()
        this._scrollTo(0)
        break
      case "End":
        event.preventDefault()
        this._scrollTo(this.slideTargets.length - 1)
        break
    }
  }

  // ============================================================================
  // INTERNALS
  // ============================================================================

  _step(direction) {
    const target = stepIndex({
      active: this._activeIndex,
      count: this.slideTargets.length,
      perView: this._perView,
      direction,
      loop: this.loopValue
    })
    if (target !== null) this._scrollTo(target)
  }

  _scrollTo(index) {
    const slide = this.slideTargets[index]
    if (!slide || !this.hasTrackTarget) return

    this.trackTarget.scrollTo({ left: this._slideOffsets()[index], behavior: this._motionSuppressed() ? "auto" : "smooth" })
  }

  // Active slide follows the scroll position (arrows, swipe, keyboard alike)
  _setupScrollSync() {
    if (!this.hasTrackTarget) return

    this._scrollHandler = () => {
      if (this._scrollFrame) return
      this._scrollFrame = window.requestAnimationFrame(() => {
        this._scrollFrame = null
        this._syncActiveFromScroll()
      })
    }
    this.trackTarget.addEventListener("scroll", this._scrollHandler, { passive: true })
  }

  _syncActiveFromScroll() {
    const offsets = this._slideOffsets()
    if (offsets.length === 0) return
    const idx = nearestIndex(offsets, this.trackTarget.scrollLeft)
    if (idx !== this._activeIndex) this._updateActive(idx)
  }

  // Scroll position that brings each slide to the start. Measured from the
  // first slide, so track padding (bleed:) doesn't shift every stop.
  _slideOffsets() {
    const first = this.slideTargets[0]
    if (!first) return []
    return this.slideTargets.map((slide) => slide.offsetLeft - first.offsetLeft)
  }

  _updateActive(index, { announce = true } = {}) {
    this._activeIndex = index
    const total = this.slideTargets.length

    // Indicators
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((dot, i) => {
        dot.setAttribute("aria-current", i === index ? "true" : "false")
      })
    }

    // Slide focus management
    this.slideTargets.forEach((slide, i) => {
      slide.setAttribute("tabindex", i === index ? "0" : "-1")
    })

    // Boundary arrow state
    if (!this.loopValue) {
      if (this.hasPrevTarget) {
        this.prevTargets.forEach((b) => {
          const disabled = index <= 0
          b.disabled = disabled
          b.setAttribute("aria-disabled", disabled ? "true" : "false")
        })
      }
      if (this.hasNextTarget) {
        this.nextTargets.forEach((b) => {
          const disabled = index >= Math.max(0, total - this._perView)
          b.disabled = disabled
          b.setAttribute("aria-disabled", disabled ? "true" : "false")
        })
      }
    }

    // Announce
    if (announce && this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = `Slide ${index + 1} of ${total}`
    }
  }

  _startTimer() {
    this._stopTimer()
    if (this.intervalValue <= 0) return
    this._timer = window.setInterval(() => {
      this.next()
    }, this.intervalValue)
  }

  _stopTimer() {
    if (this._timer) {
      window.clearInterval(this._timer)
      this._timer = null
    }
  }

  _onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this._userPaused = true
      this._stopTimer()
    } else {
      this._userPaused = false
      if (this.autoRotateValue && !this._hoverPaused && !this._motionSuppressed()) {
        this._startTimer()
      }
    }
  }

  _matchesReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Single source of truth for "should motion be suppressed right now."
  // Returns true only when the OS prefers reduced motion AND the consumer
  // has not explicitly opted out via respect_reduced_motion: false.
  // Every motion gate in this controller must route through here, otherwise
  // the consumer opt-out becomes inconsistent (the gem-side bug from <=0.50.3).
  _motionSuppressed() {
    return this.respectReducedMotionValue && this._reducedMotion
  }

  _resolveItemsPerView() {
    let raw
    try {
      raw = JSON.parse(this.itemsPerViewValue)
    } catch (_e) {
      raw = 1
    }

    const count = resolveItemsPerView(raw, window.innerWidth)
    this._perView = count
    // The gap comes from the track's gap class (gap: kwarg), so read it back
    const gap = this.hasTrackTarget ? parseFloat(window.getComputedStyle(this.trackTarget).columnGap) : 0
    this.element.style.setProperty("--carousel-slide-basis", slideBasis(count, gap))
    this._syncControls()
  }

  // One dot per reachable position; no arrows or dots when every slide is
  // already in view. Inline display, not [hidden]: a display utility on the
  // element would beat [hidden] under Tailwind v3's preflight.
  _syncControls() {
    const positions = positionCount(this.slideTargets.length, this._perView)
    const show = (el, visible) => { el.style.display = visible ? "" : "none" }
    this.indicatorTargets.forEach((dot, i) => show(dot, i < positions))
    const lists = new Set(this.indicatorTargets.map((dot) => dot.parentElement))
    lists.forEach((list) => show(list, positions > 1))
    this.prevTargets.forEach((b) => show(b, positions > 1))
    this.nextTargets.forEach((b) => show(b, positions > 1))
  }

  _debounce(fn, wait) {
    let t
    return (...args) => {
      window.clearTimeout(t)
      t = window.setTimeout(() => fn.apply(this, args), wait)
    }
  }
}
